import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingCart, Search, User } from 'lucide-react';
import { getCartCount } from '../utils/cart';
import api from '../utils/api';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [subcategoryMap, setSubcategoryMap] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const navigate = useNavigate();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    setCartCount(getCartCount());
    const handleStorageChange = () => setCartCount(getCartCount());
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cartUpdated', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories?visible=true');
        setCategories(response.data);
        // Fetch subcategories for all categories
        const subMap = {};
        await Promise.all(
          response.data.map(async (cat) => {
            try {
              const subRes = await api.get(`/subcategories?category_slug=${cat.slug}`);
              if (subRes.data && subRes.data.length > 0) {
                subMap[cat.slug] = subRes.data;
              }
            } catch (e) {}
          })
        );
        setSubcategoryMap(subMap);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      data-testid="navigation-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'backdrop-blur-xl bg-[#FFFDF9]/90 shadow-md border-b border-[#E5A93C]/20'
          : 'bg-[#FFFDF9] border-b border-[#E5A93C]/10'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_ae32ccb0-3c5f-414a-b46c-cfd49c04a91f/artifacts/kjz9vxob_556719997_17915806479189788_8925177708886366329_n.jpg"
              alt="Ahmedabad NX Logo"
              className="h-12 w-12 object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors font-medium" data-testid="nav-home">Home</Link>
            <Link to="/about" className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors font-medium" data-testid="nav-about">About</Link>

            {/* Collections Dropdown */}
            <div className="relative group">
              <button
                className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors font-medium flex items-center"
                data-testid="nav-collections"
              >
                Collections
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <div className="absolute left-0 mt-2 w-64 bg-white border border-[#E5A93C]/20 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 rounded-sm">
                <div className="py-2">
                  {categories.map((category) => {
                    const subs = subcategoryMap[category.slug] || [];
                    return (
                      <div
                        key={category.slug}
                        className="relative"
                        onMouseEnter={() => setHoveredCategory(category.slug)}
                        onMouseLeave={() => setHoveredCategory(null)}
                      >
                        <div className="flex items-center justify-between px-4 py-2 text-[#2C2C2C] hover:bg-[#E5A93C]/10 hover:text-[#E5A93C] transition-colors group/item">
                          <Link
                            to={`/collections/${category.slug}`}
                            className="flex-1"
                            data-testid={`category-${category.slug}-link`}
                          >
                            {category.name}
                          </Link>
                          {subs.length > 0 && (
                            <svg className="w-3 h-3 ml-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </div>

                        {/* Subcategory flyout — shown on hover */}
                        {subs.length > 0 && hoveredCategory === category.slug && (
                          <div className="absolute left-full top-0 w-44 bg-white border border-[#E5A93C]/20 shadow-xl z-50 rounded-sm">
                            <div className="py-1">
                              {/* "All" link */}
                              <Link
                                to={`/collections/${category.slug}`}
                                className="block px-4 py-2 text-sm text-[#2C2C2C] hover:bg-[#E5A93C]/10 hover:text-[#E5A93C] transition-colors font-medium"
                              >
                                All {category.name}
                              </Link>
                              <div className="border-t border-gray-100 my-1" />
                              {subs.map(sub => (
                                <Link
                                  key={sub.slug}
                                  to={`/collections/${category.slug}?subcategory=${sub.slug}`}
                                  className="flex items-center px-4 py-2 text-sm text-[#2C2C2C] hover:bg-[#E5A93C]/10 hover:text-[#E5A93C] transition-colors"
                                >
                                  {/* Bold badge for big sizes */}
                                  {['3xl', '4xl', '5xl'].includes(sub.slug) ? (
                                    <span className="mr-2 px-1.5 py-0.5 text-xs font-bold bg-[#2A0000] text-white rounded">
                                      {sub.name}
                                    </span>
                                  ) : (
                                    <span className="mr-2 w-1.5 h-1.5 rounded-full bg-[#E5A93C] flex-shrink-0" />
                                  )}
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <Link to="/blogs" className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors font-medium" data-testid="nav-blogs">Blogs</Link>
            <Link to="/contact" className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors font-medium" data-testid="nav-contact">Contact</Link>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors"
              data-testid="search-button"
            >
              <Search className="w-5 h-5" />
            </button>

            <Link
              to="/cart"
              className="relative text-[#2C2C2C] hover:text-[#E5A93C] transition-colors"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#E5A93C] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>

            <Link
              to="/admin/login"
              className="hidden md:block text-[#2C2C2C] hover:text-[#E5A93C] transition-colors"
              data-testid="admin-button"
            >
              <User className="w-5 h-5" />
            </Link>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-[#2C2C2C]"
              data-testid="mobile-menu-button"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {showSearch && (
          <div className="py-4 border-t border-[#E5A93C]/10" data-testid="search-bar">
            <form onSubmit={handleSearchSubmit} className="flex space-x-2 max-w-xl mx-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, SKU, category, or color..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#E5A93C]"
                autoFocus
                data-testid="search-input-header"
              />
              <button type="submit" className="btn-primary px-6 py-2">Search</button>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-[#E5A93C]/10" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-2">
              <Link to="/" className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors py-1" onClick={() => setIsMenuOpen(false)}>Home</Link>
              <Link to="/about" className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors py-1" onClick={() => setIsMenuOpen(false)}>About</Link>

              <div className="pl-2 space-y-1">
                <p className="text-sm font-semibold text-[#2A0000] pt-1">Collections:</p>
                {categories.map((category) => {
                  const subs = subcategoryMap[category.slug] || [];
                  return (
                    <div key={category.slug}>
                      <Link
                        to={`/collections/${category.slug}`}
                        className="block text-[#2C2C2C] hover:text-[#E5A93C] transition-colors py-1 pl-2"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {category.name}
                      </Link>
                      {/* Mobile subcategories */}
                      {subs.length > 0 && (
                        <div className="pl-4 space-y-0.5">
                          {subs.map(sub => (
                            <Link
                              key={sub.slug}
                              to={`/collections/${category.slug}?subcategory=${sub.slug}`}
                              className="flex items-center text-sm text-gray-500 hover:text-[#E5A93C] transition-colors py-0.5"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {['3xl', '4xl', '5xl'].includes(sub.slug) ? (
                                <span className="mr-1.5 px-1 py-0.5 text-xs font-bold bg-[#2A0000] text-white rounded">
                                  {sub.name}
                                </span>
                              ) : (
                                <span className="mr-1.5 w-1 h-1 rounded-full bg-[#E5A93C] flex-shrink-0 inline-block" />
                              )}
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <Link to="/blogs" className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors py-1" onClick={() => setIsMenuOpen(false)}>Blogs</Link>
              <Link to="/contact" className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors py-1" onClick={() => setIsMenuOpen(false)}>Contact</Link>
              <Link to="/admin/login" className="text-[#2C2C2C] hover:text-[#E5A93C] transition-colors py-1" onClick={() => setIsMenuOpen(false)}>Admin</Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navigation;